/*
 * Copyright (C) 2024 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import {
  AdministrationOrganisationsEinheit,
  AdministrationOrganisationsEinheitService,
} from '@administration-client/organisations-einheit-shared';
import { Routes } from '@administration-client/shared';
import { ClientRoles, RoleDescriptions, RoleLabels, User, UserService } from '@administration-client/user-shared';
import { KeycloakErrorMessage, KeycloakFieldName, KeycloakFormService, PatchConfig } from '@administration/keycloak-shared';
import { NavigationService } from '@alfa-client/navigation-shared';
import {
  EMPTY_STRING,
  fieldEmptyValidator,
  fieldInvalidValidator,
  fieldLengthValidator,
  InvalidParam,
  isLoaded,
  mapToResource,
  StateResource,
  ValidationMessageCode,
} from '@common/tech-shared';
import { SnackBarService } from '@ui';
import { inject, Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';
import { UrlSegment } from '@angular/router';
import { filter, Observable, Subject, take, tap } from 'rxjs';

@Injectable()
export class UserFormService extends KeycloakFormService<User> {
  protected readonly formBuilder = inject(UntypedFormBuilder);
  private readonly userService = inject(UserService);
  private readonly adminOrganisationsEinheitService = inject(AdministrationOrganisationsEinheitService);
  private readonly navigationService = inject(NavigationService);
  private readonly snackBarService = inject(SnackBarService);

  public static readonly FIRST_NAME: string = 'firstName';
  public static readonly LAST_NAME: string = 'lastName';
  public static readonly USERNAME: string = 'username';
  public static readonly EMAIL: string = 'email';

  public static readonly CLIENT_ROLES: string = 'clientRoles';
  public static readonly GROUPS: string = 'groups';
  public static readonly ADMINISTRATION_GROUP: string = 'admin';
  public static readonly ALFA_GROUP: string = 'alfa';
  public static readonly ROLE_NAME: string = 'name';
  public static readonly ROLE_CHECKED: string = 'checked';

  _organisationsEinheitToGroupIdMap: Map<string, string> = new Map<string, string>();

  private patchedRoles: ClientRoles = { admin: [], alfa: [] };

  private readonly submittedSubject: Subject<void> = new Subject<void>();

  public readonly submitted$: Observable<void> = this.submittedSubject.asObservable();

  constructor() {
    super();
    this.init();
  }

  override submit(): Observable<StateResource<User>> {
    const result: Observable<StateResource<User>> = super.submit();
    this.submittedSubject.next();
    return result;
  }

  init() {
    this._initOrganisationsEinheiten().pipe(take(1)).subscribe();
  }

  _buildPatchConfig(url: UrlSegment[]): PatchConfig {
    const doPatch: boolean = this.shouldPatch(url);
    return {
      id: doPatch ? url[1].path : undefined,
      doPatch,
    };
  }

  private shouldPatch(url: UrlSegment[]): boolean {
    return url.length == 2 && url[0].path == 'benutzer' && url[1].path != 'neu';
  }

  _load(id: string): Observable<StateResource<User>> {
    return this.userService.getUserById(id);
  }

  override _patch(user: User): void {
    this.patchedRoles = user.clientRoles ?? { admin: [], alfa: [] };
    const availableAdminRoles: string[] = this._getRoleNames(UserFormService.ADMINISTRATION_GROUP);
    const availableAlfaRoles: string[] = this._getRoleNames(UserFormService.ALFA_GROUP);

    super._patch({ ...user, clientRoles: { admin: [], alfa: [] } });

    this.setAvailableRoles(UserFormService.ADMINISTRATION_GROUP, availableAdminRoles);
    this.setAvailableRoles(UserFormService.ALFA_GROUP, availableAlfaRoles);
  }

  private _getRoleNames(group: string): string[] {
    return this.getRoleArray(group).controls.map((roleGroup: AbstractControl) => this.getRoleName(roleGroup));
  }

  public getAvailableRoles(): Observable<ClientRoles> {
    return this.userService.getAvailableRoles().pipe(filter(isLoaded), mapToResource<ClientRoles>());
  }

  _initForm(): UntypedFormGroup {
    return this.formBuilder.group(
      {
        [UserFormService.FIRST_NAME]: new FormControl(EMPTY_STRING, fieldEmptyValidator(UserFormService.FIRST_NAME)),
        [UserFormService.LAST_NAME]: new FormControl(EMPTY_STRING, fieldEmptyValidator(UserFormService.LAST_NAME)),
        [UserFormService.USERNAME]: new FormControl(EMPTY_STRING, [fieldLengthValidator(UserFormService.USERNAME, 3, 255)]),
        [UserFormService.EMAIL]: new FormControl(EMPTY_STRING, [
          fieldInvalidValidator(UserFormService.EMAIL, /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/),
        ]),
        [UserFormService.CLIENT_ROLES]: this.formBuilder.group(
          {
            [UserFormService.ADMINISTRATION_GROUP]: this.formBuilder.array([]),
            [UserFormService.ALFA_GROUP]: this.formBuilder.array([]),
          },
          {
            validators: clientRolesEmptyValidator(UserFormService.CLIENT_ROLES, [
              UserFormService.ADMINISTRATION_GROUP,
              UserFormService.ALFA_GROUP,
            ]),
          },
        ),
        [UserFormService.GROUPS]: this.formBuilder.group({}),
      },
      { updateOn: 'submit' },
    );
  }

  _initOrganisationsEinheiten(): Observable<AdministrationOrganisationsEinheit[]> {
    const organisationsEinheitenGroup: UntypedFormGroup = this.getOrganisationsEinheitenGroup();
    return this.adminOrganisationsEinheitService.getAll().pipe(
      filter(isLoaded),
      mapToResource<AdministrationOrganisationsEinheit[]>(),
      tap((organisationsEinheiten: AdministrationOrganisationsEinheit[]): void => {
        this.setOrganisationsEinheitenIdsInMap(organisationsEinheiten);
        this._addOrganisationsEinheitenToForm(organisationsEinheiten, organisationsEinheitenGroup);
      }),
    );
  }

  private setOrganisationsEinheitenIdsInMap(organisationsEinheiten: AdministrationOrganisationsEinheit[]): void {
    organisationsEinheiten.forEach((organisationsEinheit: AdministrationOrganisationsEinheit) => {
      this._organisationsEinheitToGroupIdMap.set(organisationsEinheit.name, organisationsEinheit.id);
    });
  }

  _addOrganisationsEinheitenToForm(
    organisationsEinheiten: AdministrationOrganisationsEinheit[],
    organisationsEinheitenGroup: UntypedFormGroup,
  ): void {
    organisationsEinheiten.forEach((organisationsEinheit: AdministrationOrganisationsEinheit) => {
      organisationsEinheitenGroup.addControl(organisationsEinheit.name, new FormControl(false));
    });
  }

  public setAvailableRoles(group: string, roleNames: string[]): void {
    const roleArray: FormArray = this.getRoleArray(group);
    roleArray.clear();
    roleNames.forEach((roleName: string) => roleArray.push(this._createRoleGroup(group, roleName)));
  }

  private _createRoleGroup(group: string, roleName: string): FormGroup {
    return this.formBuilder.group({
      [UserFormService.ROLE_NAME]: new FormControl(roleName),
      [UserFormService.ROLE_CHECKED]: new FormControl(this._isRolePatched(group, roleName), { updateOn: 'change' }),
    });
  }

  private _isRolePatched(group: string, roleName: string): boolean {
    return this._getPatchedRoleNames(group).includes(roleName);
  }

  private _getPatchedRoleNames(group: string): string[] {
    return group === UserFormService.ALFA_GROUP ? this.patchedRoles.alfa : this.patchedRoles.admin;
  }

  public getRoleArray(group: string): FormArray {
    return this.form.get(UserFormService.CLIENT_ROLES).get(group) as FormArray;
  }

  public getCheckedControl(roleGroup: AbstractControl): FormControl {
    return roleGroup.get(UserFormService.ROLE_CHECKED) as FormControl;
  }

  public getRoleName(roleGroup: AbstractControl): string {
    return roleGroup.get(UserFormService.ROLE_NAME).value;
  }

  public getRoleLabel(roleGroup: AbstractControl): string {
    const roleName: string = this.getRoleName(roleGroup);
    return RoleLabels[roleName] ?? roleName;
  }

  public getRoleDescription(roleGroup: AbstractControl): string {
    return RoleDescriptions[this.getRoleName(roleGroup)];
  }

  public isAlfaRoleDisabled(roleGroup: AbstractControl): boolean {
    return this._isAnyAlfaRoleAssigned() && !this.getCheckedControl(roleGroup).value;
  }

  _isAnyAlfaRoleAssigned(): boolean {
    return this.getRoleArray(UserFormService.ALFA_GROUP).controls.some(
      (roleGroup: AbstractControl) => this.getCheckedControl(roleGroup).value,
    );
  }

  doSubmit(): Observable<StateResource<User>> {
    const user: User = this._createUser();
    return this._createOrSave(user).pipe(
      tap((state: StateResource<User>): void => {
        if (!state.loading) this.handleOnCreateUserSuccess();
      }),
    );
  }

  _createOrSave(user: User): Observable<StateResource<User>> {
    if (this.isPatch()) {
      return this.userService.save({
        ...user,
        id: this._patchConfig.id,
        groupIds: user.groups.map((groupName: string): string => this._organisationsEinheitToGroupIdMap.get(groupName)),
      });
    } else {
      return this.userService.create(user);
    }
  }

  handleOnCreateUserSuccess(): void {
    this.snackBarService.showInfo(this.isPatch() ? 'Der Benutzer wurde gespeichert.' : 'Der Benutzer wurde angelegt.');
    this.navigationService.navigate(Routes.BENUTZER);
  }

  _createUser(): User {
    return {
      ...this._getFormValue(),
      enabled: true,
      clientRoles: {
        alfa: this._getRoles(UserFormService.ALFA_GROUP),
        admin: this._getRoles(UserFormService.ADMINISTRATION_GROUP),
      },
      groups: this._getActiveOrganisationsEinheiten(),
    };
  }

  _getRoles(roleGroup: string): string[] {
    return this.getRoleArray(roleGroup)
      .controls.filter((role: AbstractControl) => this.getCheckedControl(role).value)
      .map((role: AbstractControl) => this.getRoleName(role));
  }

  _getActiveOrganisationsEinheiten(): string[] {
    return this.getActive(this.getOrganisationsEinheitenGroup());
  }

  private getActive(formGroup: UntypedFormGroup): string[] {
    return Object.keys(formGroup.controls).filter((name: string) => formGroup.controls[name].value);
  }

  public getOrganisationsEinheitenGroup(): UntypedFormGroup {
    return <UntypedFormGroup>this.form.get(UserFormService.GROUPS);
  }

  public getUserName(): string {
    return this.form.get(UserFormService.USERNAME).value;
  }

  _mapKeycloakErrorMessageToControlName(keycloakErrorMessage: KeycloakErrorMessage): string {
    switch (keycloakErrorMessage) {
      case KeycloakErrorMessage.USERNAME_IS_MISSING:
      case KeycloakErrorMessage.USERNAME_EXISTS:
        return UserFormService.USERNAME;
      case KeycloakErrorMessage.EMAIL_EXISTS:
        return UserFormService.EMAIL;
      default:
        throw new Error(`Cannot map keycloak error message ${keycloakErrorMessage} to control name`);
    }
  }

  _mapKeycloakFieldNameToControlName(keycloakFieldName: KeycloakFieldName): string {
    switch (keycloakFieldName) {
      case KeycloakFieldName.USERNAME:
        return UserFormService.USERNAME;
      case KeycloakFieldName.EMAIL:
        return UserFormService.EMAIL;
      default:
        throw new Error(`Cannot map keycloak field name ${keycloakFieldName} to control name`);
    }
  }

  _handleUnknownKeycloakError(): void {
    this.snackBarService.showError(
      this.isPatch() ? 'Der Benutzer konnte nicht gespeichert werden.' : 'Der Benutzer konnte nicht angelegt werden.',
    );
  }
}

export function clientRolesEmptyValidator(controlName: string, groupNames: string[]): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const group: FormGroup = control as FormGroup;
    const anyRoleChecked: boolean = groupNames.some((groupName: string) => isAnyRoleChecked(group.get(groupName) as FormArray));
    return anyRoleChecked ? null : buildRolesEmptyError(controlName);
  };
}

function isAnyRoleChecked(roleArray: FormArray): boolean {
  return (roleArray?.controls ?? []).some(
    (roleGroup: AbstractControl) => roleGroup.get(UserFormService.ROLE_CHECKED)?.value === true,
  );
}

function buildRolesEmptyError(controlName: string): ValidationErrors {
  return {
    [ValidationMessageCode.FIELD_EMPTY]: {
      name: controlName,
      value: null,
      reason: ValidationMessageCode.FIELD_EMPTY,
      constraintParameters: [],
    } as InvalidParam,
  };
}
